import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { X, Loader2, AlertTriangle, ClipboardList, Package, Ruler } from 'lucide-react';
import { toast } from 'sonner';
import CreateIssueModal, { deviationPrefill, sourceImageAttachment } from './CreateIssueModal';
import IssueDetailModal from './IssueDetailModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const STATUS_STYLE = {
  open: 'bg-blue-100 text-blue-700', in_progress: 'bg-amber-100 text-amber-700',
  resolved: 'bg-green-100 text-green-700', closed: 'bg-slate-200 text-slate-600',
};

const ProjectDrilldownModal = ({ project, open, onClose }) => {
  const [tab, setTab] = useState('jobs');
  const [jobs, setJobs] = useState([]);
  const [deviations, setDeviations] = useState([]);
  const [issues, setIssues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [issueCtx, setIssueCtx] = useState(null);
  const [detailId, setDetailId] = useState(null);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  const load = async () => {
    setLoading(true);
    try {
      const [j, d, i] = await Promise.all([
        axios.get(`${API}/projects/${project.projectId}/analysis-jobs`),
        axios.get(`${API}/projects/${project.projectId}/deviations`),
        axios.get(`${API}/projects/${project.projectId}/issues`),
      ]);
      setJobs(j.data || []);
      setDeviations(d.data?.deviations || []);
      setIssues(i.data || []);
    } catch (e) { toast.error('Failed to load project detail'); } finally { setLoading(false); }
  };

  useEffect(() => {
    if (open && project) { setTab('jobs'); load(); }
    // eslint-disable-next-line
  }, [open, project]);

  if (!open || !project) return null;

  const openDeviationIssue = async (dev) => {
    try {
      const jobRes = await axios.get(`${API}/analysis-jobs/${dev.jobId}`);
      const jobFull = jobRes.data;
      const pre = deviationPrefill(jobFull);
      const photo = await sourceImageAttachment(project.projectId, jobFull.inputUploadId || jobFull.uploadId);
      setIssueCtx({
        job: jobFull, titleOverride: pre.title, descriptionOverride: pre.description,
        initialAttachments: photo ? [photo] : [], initialAssignee: jobFull.createdBy || '',
      });
    } catch (e) { toast.error('Could not open issue from deviation'); }
  };

  const TabBtn = ({ id, label, count, icon: Icon }) => (
    <button onClick={() => setTab(id)} className={`px-4 py-2 text-sm font-medium flex items-center gap-2 border-b-2 ${tab === id ? 'border-indigo-500 text-indigo-600' : 'border-transparent text-slate-500 hover:text-slate-700'}`} data-testid={`drilldown-tab-${id}`}>
      <Icon className="w-4 h-4" /> {label} <span className="text-xs bg-slate-100 px-1.5 rounded-full">{count}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" data-testid="project-drilldown-modal">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-200">
          <div>
            <h3 className="text-lg font-semibold text-slate-900">{project.projectName}</h3>
            <p className="text-xs text-slate-500">{project.completedJobs}/{project.totalJobs} jobs · {project.flaggedDeviations} flagged · {project.openIssues} open issues</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600" data-testid="drilldown-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="flex gap-2 px-5 border-b border-slate-100">
          <TabBtn id="jobs" label="Jobs" count={jobs.length} icon={Package} />
          <TabBtn id="deviations" label="Deviations" count={deviations.length} icon={AlertTriangle} />
          <TabBtn id="issues" label="Issues" count={issues.length} icon={ClipboardList} />
        </div>

        <div className="p-5 overflow-y-auto flex-1">
          {loading ? (
            <div className="flex items-center justify-center py-12 text-slate-500"><Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading…</div>
          ) : tab === 'jobs' ? (
            <div className="space-y-2" data-testid="drilldown-jobs">
              {jobs.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No jobs yet.</p>}
              {jobs.map((j) => (
                <div key={j.id} className="flex items-center justify-between border border-slate-200 rounded-lg p-3">
                  <div className="flex items-center gap-2 min-w-0">
                    {(j.type || '').includes('MEASURE') ? <Ruler className="w-4 h-4 text-teal-500" /> : <Package className="w-4 h-4 text-indigo-500" />}
                    <span className="text-sm font-medium text-slate-800 truncate">{j.roomName || j.result?.roomName || j.type}</span>
                    <span className="text-xs text-slate-400 font-mono">{j.id.slice(0, 8)}</span>
                  </div>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${j.status === 'completed' ? 'bg-green-100 text-green-700' : j.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>{j.status}</span>
                </div>
              ))}
            </div>
          ) : tab === 'deviations' ? (
            <div className="space-y-2" data-testid="drilldown-deviations">
              {deviations.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No flagged deviations.</p>}
              {deviations.map((d) => (
                <div key={d.jobId} className="flex items-center justify-between border border-red-200 rounded-lg p-3">
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-slate-800 truncate">{d.roomName || d.type}</p>
                    <p className="text-xs text-slate-500">Expected {d.deviation.expected} · Actual {d.deviation.actual}</p>
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <span className="px-2 py-1 rounded-full text-xs font-semibold bg-red-100 text-red-700">{d.deviation.percent > 0 ? '+' : ''}{d.deviation.percent}%</span>
                    <button onClick={() => openDeviationIssue(d)} className="px-2.5 py-1 bg-amber-600 text-white rounded-lg text-xs hover:bg-amber-700" data-testid="drilldown-deviation-issue-btn">Create Issue</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="space-y-2" data-testid="drilldown-issues">
              {issues.length === 0 && <p className="text-sm text-slate-400 text-center py-8">No issues.</p>}
              {issues.map((i) => (
                <button key={i.id} onClick={() => setDetailId(i.id)} className="w-full text-left flex items-center justify-between border border-slate-200 rounded-lg p-3 hover:bg-slate-50" data-testid="drilldown-issue-row">
                  <span className="text-sm font-medium text-slate-800 truncate">{i.title}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full ${STATUS_STYLE[i.status]}`}>{i.status.replace('_', ' ')}</span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      <CreateIssueModal
        projectId={project.projectId}
        job={issueCtx?.job}
        titleOverride={issueCtx?.titleOverride}
        descriptionOverride={issueCtx?.descriptionOverride}
        initialAttachments={issueCtx?.initialAttachments}
        initialAssignee={issueCtx?.initialAssignee}
        open={!!issueCtx}
        onClose={() => setIssueCtx(null)}
        onCreated={() => { toast.success('Punch-list issue created from deviation'); load(); }}
      />
      <IssueDetailModal issueId={detailId} currentUser={currentUser} open={!!detailId} onClose={() => setDetailId(null)} onChanged={load} />
    </div>
  );
};

export default ProjectDrilldownModal;
